import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ArrowUpRight } from "lucide-react";
import Logo, { LogoText } from "@/components/site/Logo";

const navLinks = [
  { label: "Home", to: "/", testId: "nav-home" },
  { label: "Services", to: "/services", testId: "nav-services" },
  { label: "Book Now", to: "/book", testId: "nav-book" },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#FBF9F6]/85 backdrop-blur-md border-b border-black/10"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between min-h-32 md:min-h-44 py-2">
          <Logo />

          <nav className="hidden md:flex items-center gap-10">
            {navLinks.slice(0, 2).map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                className={({ isActive }) =>
                  `text-sm uppercase tracking-[0.18em] font-medium transition-colors ${
                    isActive ? "text-orange-500" : "text-black hover:text-orange-500"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/book"
              data-testid="nav-book-cta"
              className="group inline-flex items-center gap-2 bg-black text-[#FBF9F6] px-5 py-3 text-xs uppercase tracking-[0.18em] font-bold border-2 border-black hover:bg-orange-500 hover:border-orange-500 transition-colors"
            >
              Book Now
              <ArrowUpRight className="w-4 h-4 transition-transform group-hover:rotate-45" strokeWidth={2.5} />
            </Link>
          </nav>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-mobile-toggle"
            className="md:hidden inline-flex items-center justify-center w-11 h-11 border-2 border-black text-black"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-[#FBF9F6] border-t border-black/10" data-testid="mobile-menu">
          <div className="px-6 py-6 flex flex-col gap-5">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`${l.testId}-mobile`}
                className={({ isActive }) =>
                  `text-base uppercase tracking-[0.18em] font-semibold ${
                    isActive ? "text-orange-500" : "text-black"
                  }`
                }
                end={l.to === "/"}
              >
                {l.label}
              </NavLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

const Footer = () => {
  return (
    <footer data-testid="site-footer" className="bg-black text-[#FBF9F6] mt-0">
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-16 py-20">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-6">
            <LogoText tagline />
            <p className="mt-8 text-orange-400 text-xs uppercase tracking-[0.25em] font-bold mb-4">
              The last spreadsheet you'll ever need
            </p>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-black leading-[1.05] tracking-tight">
              Your child's college, <em className="italic text-yellow-400">fully funded</em>.
            </h2>
            <p className="mt-6 text-[#FBF9F6]/75 text-lg max-w-xl">
              Book a 20-minute clarity call. We'll talk strategy, scholarships, and what's actually possible for your family.
            </p>
            <Link
              to="/book"
              data-testid="footer-book-cta"
              className="mt-8 inline-flex items-center gap-3 bg-orange-500 text-black px-7 py-4 text-sm uppercase tracking-[0.18em] font-bold border-2 border-orange-500 hover:bg-yellow-400 hover:border-yellow-400 transition-colors"
            >
              Book a consult
              <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FBF9F6]/60 mb-5">Navigate</p>
            <ul className="space-y-3">
              {navLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    data-testid={`footer-${l.testId}`}
                    className="text-[#FBF9F6] hover:text-orange-400 transition-colors text-base"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-3">
            <p className="text-xs uppercase tracking-[0.25em] font-bold text-[#FBF9F6]/60 mb-5">Contact</p>
            <a
              href="https://lunacal.ai/geniedawkins/discovery-call"
              target="_blank"
              rel="noreferrer"
              data-testid="footer-lunacal-link"
              className="block text-[#FBF9F6] hover:text-orange-400 transition-colors text-base mb-3"
            >
              lunacal.ai/geniedawkins/discovery-call
            </a>
            <p className="text-[#FBF9F6]/60 text-sm leading-relaxed">
              Serving families nationwide. Building generational futures, one acceptance letter at a time.
            </p>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/15 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <p className="text-sm text-[#FBF9F6]/55">
            © {new Date().getFullYear()} Scholar Mosaic by Genie Dawkins. All rights reserved.
          </p>
          <p className="font-serif italic text-[#FBF9F6]/55 text-sm">
            "From guessing to fully funded."
          </p>
        </div>
      </div>
    </footer>
  );
};

const Layout = ({ children }) => {
  const location = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" in window ? "instant" : "auto" });
  }, [location.pathname]);

  return (
    <div className="min-h-screen bg-[#FBF9F6] flex flex-col" data-testid="site-shell">
      <Navbar />
      <main className="flex-1 pt-24 md:pt-28" data-testid="site-main">
        {children}
      </main>
      <Footer />
    </div>
  );
};

export default Layout;
